import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { hasFeature } from '../context/accountUtils';
import { showToast, removeToast } from '../context/ToastHandle';

const RECENT_TILE_COLOURS_KEY = 'iboardRecentTileColours';
const MAX_RECENT_TILE_COLOURS = 8;

const normaliseHex = (value, fallback = '#eeeeee') => {
  const raw = String(value || '').trim();
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    return `#${raw.slice(1).split('').map((c) => c + c).join('')}`.toLowerCase();
  }
  return fallback;
};

const hexToHsv = (hex) => {
  const value = normaliseHex(hex).slice(1);
  const r = parseInt(value.slice(0, 2), 16) / 255;
  const g = parseInt(value.slice(2, 4), 16) / 255;
  const b = parseInt(value.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;

  if (d !== 0) {
    if (max === r) h = 60 * (((g - b) / d) % 6);
    else if (max === g) h = 60 * (((b - r) / d) + 2);
    else h = 60 * (((r - g) / d) + 4);
  }
  if (h < 0) h += 360;

  return { h, s: max === 0 ? 0 : d / max, v: max };
};

const hsvToHex = (h, s, v) => {
  const hue = ((Number(h) % 360) + 360) % 360;
  const sat = Math.max(0, Math.min(1, Number(s)));
  const val = Math.max(0, Math.min(1, Number(v)));
  const c = val * sat;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = val - c;
  let rp = 0, gp = 0, bp = 0;

  if (hue < 60) [rp, gp, bp] = [c, x, 0];
  else if (hue < 120) [rp, gp, bp] = [x, c, 0];
  else if (hue < 180) [rp, gp, bp] = [0, c, x];
  else if (hue < 240) [rp, gp, bp] = [0, x, c];
  else if (hue < 300) [rp, gp, bp] = [x, 0, c];
  else [rp, gp, bp] = [c, 0, x];

  const toHex = (n) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(rp)}${toHex(gp)}${toHex(bp)}`;
};

const getRecentTileColours = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENT_TILE_COLOURS_KEY) || '[]');
    return Array.isArray(stored)
      ? stored.map((c) => normaliseHex(c, '')).filter(Boolean).slice(0, MAX_RECENT_TILE_COLOURS)
      : [];
  } catch {
    return [];
  }
};

const rememberTileColour = (hex) => {
  const colour = normaliseHex(hex);
  const next = [
    colour,
    ...getRecentTileColours().filter((item) => item !== colour),
  ].slice(0, MAX_RECENT_TILE_COLOURS);

  try {
    localStorage.setItem(RECENT_TILE_COLOURS_KEY, JSON.stringify(next));
  } catch {}

  return next;
};

const TileEditorModal = ({ boardId, tile, onClose, onTileSaved, onTileDel }) => {
  const { user } = useAuth();
  const [label, setLabel] = useState(tile?.label || '');
  const [color, setColor] = useState(tile?.color || '#eeeeee');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [recentColors, setRecentColors] = useState(() => getRecentTileColours());
  const [pickerHsv, setPickerHsv] = useState(() => hexToHsv(tile?.color || '#eeeeee'));
  const colorPickerRef = useRef(null);
  const [imageUrl, setImageUrl] = useState(tile?.image_url || '');
  const [selectedSymbolId, setSelectedSymbolId] = useState(null);
  const [position, setPosition] = useState(tile?.position || 0);
  const [isFolder, setIsFolder] = useState(tile?.is_folder || false);
  const [folderSilent, setFolderSilent] = useState(Boolean(Number(tile?.folder_silent || 0)));
  const [isBackButton, setIsBackButton] = useState(Boolean(Number(tile?.is_back_button || 0)));
  const [linkedBoardId, setLinkedBoardId] = useState(tile?.linked_board_id || null);
  const [boards, setBoards] = useState([]);
  const [arasaacSearch, setArasaacSearch] = useState('');
  const [symbols, setSymbols] = useState([]);
  const [hasUploads, setHasUploads] = useState(false);
  const [searchUploads, setSearchUploads] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [page, setPage] = useState(0);
  const [searchManuallyEdited, setSearchManuallyEdited] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const uploadInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const symbolsPerPage = 12;

  
  useEffect(() => {
    const checkUserUploads = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('https://jmew.xyz/aac/api/check_uploads.php', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.hasUploads) setHasUploads(true);
      } catch (err) {
        console.error("Failed to check uploads:", err);
      }
    };
    checkUserUploads();
  }, []);

  useEffect(() => {
    const fetchBoards = async () => {
      try {
        const res = await api.get('/boards.php');
        setBoards(res.data);
      } catch (err) {
        console.error('Failed to load boards', err);
      }
    };
    fetchBoards();
  }, []);

  // Keep the modal in sync with whichever tile is being edited and preserve
  // any existing image regardless of whether it came from ARASAAC or uploads.
  useEffect(() => {
    setLabel(tile?.label || '');
    const nextColor = normaliseHex(tile?.color || '#eeeeee');
    setColor(nextColor);
    setPickerHsv(hexToHsv(nextColor));
    setShowColorPicker(false);
    setImageUrl(tile?.image_url || '');
    setPosition(tile?.position || 0);
    setIsFolder(tile?.is_folder || false);
    setFolderSilent(Boolean(Number(tile?.folder_silent || 0)));
    setIsBackButton(Boolean(Number(tile?.is_back_button || 0)));
    setLinkedBoardId(tile?.linked_board_id || null);
    setSelectedSymbolId(null);
    setSymbols([]);
    setUploadResults([]);
    setPage(0);
    setSearchManuallyEdited(false);

    const existingImage = tile?.image_url || '';
    const match = existingImage.match(/\/pictograms\/(\d+)\//);

      if (match) {
        const symbolId = match[1];
        setSelectedSymbolId(symbolId);
      setSymbols([{ _id: symbolId }]);
    }

    setArasaacSearch(tile?.label || '');
  }, [tile?.id, tile?.image_url, tile?.label, tile?.color, tile?.position, tile?.is_folder, tile?.folder_silent, tile?.is_back_button, tile?.linked_board_id]);


  useEffect(() => {
    if (!showColorPicker) return undefined;

    const closeOnOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('touchstart', closeOnOutside, { passive: true });

    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('touchstart', closeOnOutside);
    };
  }, [showColorPicker]);

  const applyColor = (nextColor, remember = false) => {
    const normalised = normaliseHex(nextColor, color);
    setColor(normalised);
    setPickerHsv(hexToHsv(normalised));

    if (remember) {
      setRecentColors(rememberTileColour(normalised));
    }
  };

  const updatePickerFromSvEvent = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clientX = event.touches?.[0]?.clientX ?? event.clientX;
    const clientY = event.touches?.[0]?.clientY ?? event.clientY;
    const s = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const v = Math.max(0, Math.min(1, 1 - ((clientY - rect.top) / rect.height)));
    const next = { ...pickerHsv, s, v };

    setPickerHsv(next);
    setColor(hsvToHex(next.h, next.s, next.v));
  };

  const updatePickerHue = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clientY = event.touches?.[0]?.clientY ?? event.clientY;
    const h = Math.max(0, Math.min(359, ((clientY - rect.top) / rect.height) * 359));
    const next = { ...pickerHsv, h };

    setPickerHsv(next);
    setColor(hsvToHex(next.h, next.s, next.v));
  };

  const deleteTile = async (tileId) => {
    if (!tileId) return;
    try {
      onTileDel(tileId);
      onClose();
    } catch (err) {
      console.error(`Failed to delete tile: ${err.message}`);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const selectedColor = normaliseHex(color);
    setRecentColors(rememberTileColour(selectedColor));

    const tileData = {
      board_id: boardId,
      label,
      color: selectedColor,
      image_url: imageUrl,
      position,
      is_folder: isBackButton ? 0 : isFolder,
      folder_silent: !isBackButton && isFolder && folderSilent ? 1 : 0,
      is_back_button: isBackButton ? 1 : 0,
      linked_board_id: !isBackButton && isFolder ? linkedBoardId : null
    };

    try {
      let res;
      if (tile?.id) {
        res = await api.put(`/tiles.php?id=${tile.id}`, tileData);
      } else {
        res = await api.post('/tiles.php', tileData);
      }
      onTileSaved(res.data.tile);
    } catch (err) {
      console.error('Failed to save tile', err);
    }
  };

  const handleSymbolSearch = async () => {
    if (!arasaacSearch.trim()) {
      setSymbols([]);
      setUploadResults([]);
      return;
    }
  
    setPage(0); // reset pagination
  
    if (searchUploads) {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`https://jmew.xyz/aac/api/search_uploads.php?query=${encodeURIComponent(arasaacSearch.trim())}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (Array.isArray(data)) {
          setUploadResults(data);
        } else {
          console.warn('Unexpected upload search result:', data);
          setUploadResults([]);
        }
        setSymbols([]); // 🔥 hide ARASAAC
      } catch (err) {
        console.error('Failed to fetch uploaded images:', err);
        setUploadResults([]);
      }
    } else {
      // fallback to ARASAAC
      try {
        const res = await fetch(`https://api.arasaac.org/api/pictograms/en/search/${encodeURIComponent(arasaacSearch.trim())}`);
        const data = await res.json();
        if (Array.isArray(data)) {
          setSymbols(data);
        } else {
          console.warn('Unexpected ARASAAC response:', data);
          setSymbols([]);
        }
        setUploadResults([]); // 🔥 hide uploads
      } catch (err) {
        console.error('Failed to fetch ARASAAC symbols:', err);
        setSymbols([]);
      }
    }
  };

  const handleSymbolSelect = (gurl, id, type) => {
    if (type === 'user') {
      setImageUrl(gurl);
      setSelectedSymbolId(id);
    } else {
    const url = `https://static.arasaac.org/pictograms/${id}/${id}_300.png`;
    setImageUrl(url);
    setSelectedSymbolId(id);
    }
  };


  const handleUploadImageClick = () => {
    if (!hasFeature('upload')) {
      showToast('This feature is not available on your current plan.', 'error', false);
      return;
    }

    uploadInputRef.current?.click();
  };

  const handleTakePhotoClick = () => {
    if (!hasFeature('upload')) {
      showToast('This feature is not available on your current plan.', 'error', false);
      return;
    }

    cameraInputRef.current?.click();
  };

  const compressImageForUpload = async (file) => {
    const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;
    const MAX_DIMENSION = 1600;

    if (!file?.type?.startsWith('image/')) {
      throw new Error('Please select an image file.');
    }

    // Keep small, sensibly-sized files untouched so PNG transparency and
    // existing image quality are preserved where compression is unnecessary.
    const bitmap = await createImageBitmap(file);
    const needsResize =
      bitmap.width > MAX_DIMENSION ||
      bitmap.height > MAX_DIMENSION;

    if (file.size <= MAX_UPLOAD_BYTES && !needsResize) {
      bitmap.close?.();
      return file;
    }

    const scale = Math.min(
      1,
      MAX_DIMENSION / Math.max(bitmap.width, bitmap.height)
    );

    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close?.();
      throw new Error('Image compression is not supported on this device.');
    }

    // JPEG has no transparency, so use a white background before drawing.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    let quality = 0.88;
    let blob = null;

    while (quality >= 0.4) {
      blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', quality)
      );

      if (blob && blob.size <= MAX_UPLOAD_BYTES) {
        break;
      }

      quality -= 0.08;
    }

    if (!blob || blob.size > MAX_UPLOAD_BYTES) {
      throw new Error('Unable to compress this image below 2MB.');
    }

    const baseName = (file.name || 'tile-image')
      .replace(/\.[^/.]+$/, '')
      .replace(/[^\w.-]+/g, '_');

    return new File(
      [blob],
      `${baseName}.jpg`,
      {
        type: 'image/jpeg',
        lastModified: Date.now(),
      }
    );
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Keep the same plan restriction as the User Profile uploader.
    if (!hasFeature('upload')) {
      showToast('This feature is not available on your current plan.', 'error', false);
      e.target.value = '';
      return;
    }

    const userId = user?.id || localStorage.getItem('userId');
    if (!userId) {
      showToast('Upload failed: user could not be identified.', 'error', false);
      e.target.value = '';
      return;
    }

    setUploadingImage(true);
    const uploadToast = showToast(
      file.size > 2 * 1024 * 1024
        ? 'Compressing and uploading image...'
        : 'Uploading file...',
      'info',
      true
    );

    try {
      const uploadFile = await compressImageForUpload(file);

      const formData = new FormData();
      formData.append('image', uploadFile);

      const res = await fetch('https://jmew.xyz/aac/api/upload.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: formData,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        showToast('Upload failed: ' + (data.error || 'Unknown error'), 'error', false);
        return;
      }

      // upload.php stores files in /api/uploads/{userId}/ and this is the
      // same public URL format already used by Search Uploaded Images.
      const uploadedFile = `${userId}/${uploadFile.name}`;
      const uploadedUrl = `https://jmew.xyz/aac/api/uploads/${uploadedFile}`;

      // Auto-select the newly uploaded image for this tile.
      setImageUrl(uploadedUrl);
      setSelectedSymbolId(`upload-${Date.now()}`);

      // Make the upload immediately visible in the existing uploaded-images UI.
      setHasUploads(true);
      setSearchUploads(true);
      setPage(0);
      setUploadResults((prev) => [
        uploadedFile,
        ...prev.filter((existing) => existing !== uploadedFile),
      ]);
      setSymbols([]);

      showToast('Upload successful! Image selected.', 'success', false);
    } catch (err) {
      showToast('Upload error: ' + err.message, 'error', false);
    } finally {
      removeToast(uploadToast);
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const paginatedSymbols = symbols.slice(page * symbolsPerPage, (page + 1) * symbolsPerPage);
  const totalPages = Math.ceil(symbols.length / symbolsPerPage);

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3 className="modal-title">{tile ? 'Edit Tile' : 'Add Tile'}</h3>
        <form onSubmit={onSubmit}>
          <div className="modal-row">
            <input
              type="text"
              placeholder="Label"
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
                  if (!searchManuallyEdited) {
                    setArasaacSearch(e.target.value);
                  }
              }}
              className="input label-input"
              required
            />
            <div ref={colorPickerRef} style={{ position: 'relative', flex: '0 0 auto' }}>
              <button
                type="button"
                onClick={() => {
                  setPickerHsv(hexToHsv(color));
                  setRecentColors(getRecentTileColours());
                  setShowColorPicker((open) => !open);
                }}
                title={`Tile colour: ${normaliseHex(color)}`}
                aria-label={`Choose tile colour. Current colour ${normaliseHex(color)}`}
                style={{
                  width: 52,
                  height: 40,
                  padding: 4,
                  border: '1px solid #bbb',
                  borderRadius: 6,
                  background: '#fff',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                }}
              >
                <span
                  style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    borderRadius: 4,
                    background: normaliseHex(color),
                    border: '1px solid rgba(0,0,0,0.16)',
                    boxSizing: 'border-box',
                  }}
                />
              </button>

              {showColorPicker && (
                <div
                  style={{
                    position: 'absolute',
                    top: 46,
                    right: 0,
                    zIndex: 10020,
                    width: 300,
                    padding: 12,
                    border: '1px solid #cfd4da',
                    borderRadius: 10,
                    background: '#fff',
                    boxShadow: '0 10px 28px rgba(0,0,0,0.22)',
                    boxSizing: 'border-box',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
                    <span
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        background: normaliseHex(color),
                        border: '1px solid #bbb',
                        flex: '0 0 auto',
                      }}
                    />

            <input
                      type="text"
              value={color}
                      maxLength={7}
                      onChange={(e) => {
                        const value = e.target.value;
                        setColor(value);
                        if (/^#[0-9a-f]{6}$/i.test(value)) {
                          setPickerHsv(hexToHsv(value));
                        }
                      }}
                      onBlur={() => applyColor(color)}
                      aria-label="Selected tile colour hex"
                      style={{
                        width: 100,
                        padding: '8px 9px',
                        border: '1px solid #ccc',
                        borderRadius: 6,
                        fontFamily: 'monospace',
                        fontSize: 14,
                      }}
                    />

                    <button
                      type="button"
                      onClick={() => applyColor('#ffffff')}
                      title="White #ffffff"
                      style={{
                        marginLeft: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '6px 7px',
                        border: '1px solid #ccc',
                        borderRadius: 6,
                        background: '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        style={{
                          width: 19,
                          height: 19,
                          borderRadius: 4,
                          background: '#ffffff',
                          border: '1px solid #999',
                        }}
                      />
                      #ffffff
                    </button>
                  </div>

                  {recentColors.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 12, color: '#666', fontWeight: 600, marginBottom: 6 }}>
                        Recently used
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {recentColors.map((recent) => (
                          <button
                            key={recent}
                            type="button"
                            onClick={() => applyColor(recent)}
                            title={recent}
                            aria-label={`Use recent colour ${recent}`}
                            style={{
                              width: 28,
                              height: 28,
                              padding: 0,
                              border: normaliseHex(color) === recent
                                ? '3px solid #1976d2'
                                : '1px solid #999',
                              borderRadius: 6,
                              background: recent,
                              cursor: 'pointer',
                              boxSizing: 'border-box',
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 28px',
                      gap: 10,
                      alignItems: 'stretch',
                    }}
                  >
                    <div
                      role="slider"
                      aria-label="Colour saturation and brightness"
                      onMouseDown={updatePickerFromSvEvent}
                      onMouseMove={(e) => {
                        if (e.buttons === 1) updatePickerFromSvEvent(e);
                      }}
                      onTouchStart={updatePickerFromSvEvent}
                      onTouchMove={updatePickerFromSvEvent}
                      style={{
                        position: 'relative',
                        height: 150,
                        borderRadius: 7,
                        overflow: 'hidden',
                        cursor: 'crosshair',
                        touchAction: 'none',
                        background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent), hsl(${Math.round(pickerHsv.h)}, 100%, 50%)`,
                        border: '1px solid #bbb',
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          left: `${pickerHsv.s * 100}%`,
                          top: `${(1 - pickerHsv.v) * 100}%`,
                          width: 13,
                          height: 13,
                          borderRadius: '50%',
                          border: '2px solid #fff',
                          boxShadow: '0 0 0 1px #333',
                          transform: 'translate(-50%, -50%)',
                          pointerEvents: 'none',
                        }}
                      />
                    </div>

                    <div
                      role="slider"
                      aria-label="Colour hue"
                      onMouseDown={updatePickerHue}
                      onMouseMove={(e) => {
                        if (e.buttons === 1) updatePickerHue(e);
                      }}
                      onTouchStart={updatePickerHue}
                      onTouchMove={updatePickerHue}
                      style={{
                        position: 'relative',
                        height: 150,
                        borderRadius: 7,
                        cursor: 'ns-resize',
                        touchAction: 'none',
                        background: 'linear-gradient(to bottom, #ff0000 0%, #ffff00 16.66%, #00ff00 33.33%, #00ffff 50%, #0000ff 66.66%, #ff00ff 83.33%, #ff0000 100%)',
                        border: '1px solid #aaa',
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          left: -3,
                          right: -3,
                          top: `${(pickerHsv.h / 359) * 100}%`,
                          height: 3,
                          background: '#fff',
                          border: '1px solid #222',
                          borderRadius: 3,
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                        }}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 8,
                      marginTop: 10,
                    }}
                  >
                    <span style={{ fontSize: 12, color: '#666' }}>
                      Selected: <strong>{normaliseHex(color)}</strong>
                    </span>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        applyColor(color, true);
                        setShowColorPicker(false);
                      }}
                    >
                      Use colour
                    </button>
                  </div>
                </div>
              )}
            </div>
            <label className="folder-checkbox">
              <input
                type="checkbox"
                checked={isFolder}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsFolder(checked);
                  if (checked) setIsBackButton(false);
                }}
              />
              Folder
            </label>
            <label className="folder-checkbox">
              <input
                type="checkbox"
                checked={isBackButton}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsBackButton(checked);
                  if (checked) {
                    setIsFolder(false);
                    setFolderSilent(false);
                    setLinkedBoardId(null);
                    if (!label.trim()) {
                      setLabel('Back');
                      if (!searchManuallyEdited) setArasaacSearch('Back');
                    }
                    if (!imageUrl) {
                      setImageUrl('https://static.arasaac.org/pictograms/38249/38249_300.png');
                      setSelectedSymbolId('38249');
                    }
                  }
                }}
              />
              Back button
            </label>
            {isFolder && (
              <label className="folder-checkbox">
                <input
                  type="checkbox"
                  checked={folderSilent}
                  onChange={(e) => setFolderSilent(e.target.checked)}
                />
                Don't speak folder
              </label>
            )}
          </div>

          {isFolder && (
            <select
              value={linkedBoardId || ''}
              onChange={(e) => setLinkedBoardId(parseInt(e.target.value))}
              className="dropdown"
            >
              <option value="">Select linked board</option>
              {boards.map(b => (
                <option key={b.id} value={b.id}>
                  {b.name || `Board #${b.id}`}
                </option>
              ))}
            </select>
          )}

          
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '8px',
            flexWrap: 'wrap',
          }}
        >
        {hasUploads && (
            <label style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
            <input
              type="checkbox"
              checked={searchUploads}
              onChange={(e) => {
                setSearchUploads(e.target.checked);
                setSymbols([]);
                setUploadResults([]);
              }}
              style={{ marginRight: '8px' }}
            />
            Search Uploaded Images
          </label>
        )}

          <button
            type="button"
            className="btn"
            onClick={handleUploadImageClick}
            disabled={uploadingImage}
          >
            {uploadingImage ? 'Uploading…' : 'Upload Image'}
          </button>

          <button
            type="button"
            className="btn"
            onClick={handleTakePhotoClick}
            disabled={uploadingImage}
          >
            Take Photo
          </button>

          <input
            ref={uploadInputRef}
            type="file"
            accept="image/*"
            onChange={handleUploadImage}
            style={{ display: 'none' }}
          />

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleUploadImage}
            style={{ display: 'none' }}
          />
        </div>

        {imageUrl && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '10px',
              padding: '8px 10px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              background: '#fafafa',
              width: 'fit-content',
              maxWidth: '100%',
            }}
          >
            <img
              src={imageUrl}
              alt={label ? `${label} selected` : 'Selected tile image'}
              style={{
                width: 64,
                height: 64,
                objectFit: 'contain',
                borderRadius: 6,
                background: '#fff',
              }}
              onError={(e) => {
                console.warn('Failed to load existing tile image:', imageUrl);
                e.currentTarget.style.display = 'none';
              }}
            />
            <div style={{ fontSize: '0.85rem', color: '#555' }}>
              <strong style={{ display: 'block', color: '#333' }}>Current image</strong>
              This image is already assigned to the tile.
            </div>
          </div>
        )}

    <div className="arasaac-search">
    
            <input
              type="text"
              value={arasaacSearch}
              placeholder="ARASAAC search"
              onChange={(e) => {
                setArasaacSearch(e.target.value);
                setSearchManuallyEdited(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSymbolSearch();
                }
              }}
              className="input"
            />
            <button type="button" className="btn" onClick={handleSymbolSearch}>Search</button>
          </div>

          
        {searchUploads && uploadResults.length > 0 && (
          <>
            <div className="symbol-grid">
              {uploadResults.slice(page * symbolsPerPage, (page + 1) * symbolsPerPage).map((file, i) => {
                const fileUrl = `https://jmew.xyz/aac/api/uploads/${file}`;
                return (
                  <img
                    key={i}
                    src={fileUrl}
                    alt="user upload"
                    className={`symbol-preview ${imageUrl === fileUrl ? 'selected-symbol' : ''}`}
                    onClick={() => handleSymbolSelect(fileUrl, i, 'user')}
                  />
                );
              })}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
              <button type="button" className="btn gray" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>← Prev</button>
              <span style={{ fontSize: '14px', color: '#555' }}>Page {page + 1} of {Math.ceil(uploadResults.length / symbolsPerPage)}</span>
              <button type="button" className="btn gray" disabled={(page + 1) * symbolsPerPage >= uploadResults.length} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          </>
        )}

        {paginatedSymbols.length > 0 && (
            <>
              <div className="symbol-grid">
                {paginatedSymbols.map((s) => (
                  <img
                    key={s._id}
                    src={`https://static.arasaac.org/pictograms/${s._id}/${s._id}_300.png`}
                    alt=""
                    className={`symbol-preview ${selectedSymbolId === s._id ? 'selected-symbol' : ''}`}
                    onClick={() => handleSymbolSelect(s._id, s._id, 'arasaac')}
                  />
                ))}
              </div>

              <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
                <button
                  type="button"
                  className="btn gray"
                  disabled={page === 0}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                >
                  ← Prev
                </button>
                <span style={{ fontSize: '14px', color: '#555' }}>Page {page + 1} of {totalPages}</span>
                <button
                  type="button"
                  className="btn gray"
                  disabled={(page + 1) * symbolsPerPage >= symbols.length}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next →
                </button>
              </div>
            </>
          )}

          <input type="hidden" value={imageUrl} />

          <div className="modal-footer">
            <button type="button" onClick={() => deleteTile(tile.id)} className="btn red">Delete</button>
            <button type="button" onClick={onClose} className="btn gray">Cancel</button>
            <button type="submit" className="btn">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TileEditorModal;
